/**
 * Recent OMS deliveries → delivery checks. Express owns the receipt (supplier,
 * PO, products, names); the picker only sends which lines were checked, the
 * temp, the decision and any action.
 */

import moment from 'moment'
import {
  FOOD_SAFETY_DELIVERY_BATCH_MAX_CHECKS,
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_DELIVERY_TEMP_PRESETS,
  FOOD_SAFETY_SECTION,
} from 'src/constants/foodSafety'
import {
  formatDeliveryBandOptionLabel,
  formatPurchaseOrderLabel,
  guessDeliveryTempBandFromName,
  inferDeliveryTempBand,
  isAdHocTempOutOfRange,
  resolveNewDeliveryTempConfig,
} from 'src/utils/foodSafetyAdHoc'
import {
  EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
  buildFoodSafetyCorrectiveActionBody,
  validateFoodSafetyCorrectiveDraft,
} from 'src/utils/foodSafetyCorrectiveAction'

export const FOOD_SAFETY_RECEIPT_LIST_HINT =
  'Supplier and delivery details come from OMS. Open a receipt to record the products you sampled.'

export const FOOD_SAFETY_RECEIPT_OPEN_HINT =
  'Select only the products actually checked. You can record multiple products from this delivery; unselected products are not recorded as checked.'

const DELIVERIES = FOOD_SAFETY_SECTION.DELIVERIES
const { CHILLED, AMBIENT } = FOOD_SAFETY_DELIVERY_TEMP_BAND

/** Band chips with their limit: "Chilled ≤5°C", "Frozen ≤−12°C", "Ambient · no temp". */
export const FOOD_SAFETY_RECEIPT_BAND_OPTIONS = Object.freeze(
  Object.values(FOOD_SAFETY_DELIVERY_TEMP_BAND).map(band => ({
    band,
    label: formatDeliveryBandOptionLabel(band),
  })),
)

export { formatPurchaseOrderLabel }

const normaliseName = value =>
  String(value || '')
    .trim()
    .toLowerCase()

/** "today 09:12" / "yesterday 16:40" / "Thu 1 Oct 09:12". */
export function formatReceiptWhen(at, { now = moment() } = {}) {
  if (!at) return ''
  const when = moment(at)
  if (!when.isValid()) return ''
  const time = when.format('HH:mm')
  const today = moment(now).startOf('day')
  if (when.isSame(today, 'day')) return `today ${time}`
  if (when.isSame(today.clone().subtract(1, 'day'), 'day')) {
    return `yesterday ${time}`
  }
  return when.format('ddd D MMM HH:mm')
}

/** "Brakes · PO 12345" / "Brakes · PO1788" */
export function formatReceiptTitle(receipt) {
  const supplier = String(receipt?.supplier?.name || '').trim() || 'Supplier'
  return `${supplier} · ${formatPurchaseOrderLabel(
    receipt?.purchaseOrderNumber,
  )}`
}

const formatQty = n => {
  const num = Number(n)
  return Number.isInteger(num)
    ? String(num)
    : String(Math.round(num * 100) / 100)
}

/**
 * What arrived: "Received 2 × 4.5ltr", "Received 1 of 2 × 4.5ltr" when short,
 * "Received 3 kg" without a pack size. Falls back to the pack size alone.
 */
export function formatReceiptProductQuantity(product) {
  const size = String(product?.size || '').trim()
  const unit = String(product?.unit || '').trim()
  const received = product?.quantityReceived
  if (received == null || Number.isNaN(Number(received))) return size || null
  const ordered = product?.quantityOrdered
  const count =
    ordered != null && Number(ordered) !== Number(received)
      ? `${formatQty(received)} of ${formatQty(ordered)}`
      : formatQty(received)
  if (size) return `Received ${count} × ${size}`
  return `Received ${count}${unit ? ` ${unit}` : ''}`
}

/** "Receipted today 09:12 · 3 products · 2 checked" */
export function formatReceiptMeta(receipt, { now } = {}) {
  const parts = []
  const when = formatReceiptWhen(receipt?.receiptedAt, { now })
  if (when) parts.push(`Receipted ${when}`)
  const lines = Number(receipt?.receiptedLineCount) || 0
  if (lines) parts.push(`${lines} product${lines === 1 ? '' : 's'}`)
  const checked = Number(receipt?.deliveryChecks?.checkedCount) || 0
  if (checked) parts.push(`${checked} checked`)
  return parts.join(' · ')
}

export const receiptKey = receipt =>
  `${receipt?.purchaseOrderNumber || ''}|${receipt?.supplier?._id || ''}`

/** Products staff can tick: receipted and actually delivered. */
export function isReceiptProductCheckable(product) {
  return Boolean(product?.receipted) && !product?.notDelivered
}

/** Why a product can't be ticked, or null. */
export function receiptProductUnavailableReason(product) {
  if (!product?.receipted) return 'Not receipted yet'
  if (product?.notDelivered) return 'Not delivered'
  return null
}

/** A delivery template with the same product name, if the venue saved one. */
export function matchReceiptDeliveryTemplate(templates = [], productName) {
  const name = normaliseName(productName)
  if (!name) return null
  return (
    (templates || []).find(def => normaliseName(def?.name) === name) || null
  )
}

/**
 * A fresh check for one product. The band starts from its template, else a
 * guess from the product name (ice cream → Frozen), else Chilled.
 */
export function emptyReceiptCheck(template = null, productName = '') {
  const band = template
    ? inferDeliveryTempBand(template.config) || CHILLED
    : guessDeliveryTempBandFromName(productName)
  return {
    checked: false,
    value: null,
    accepted: true,
    band,
    bandGuessed: !template && band !== CHILLED,
    action: { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT },
  }
}

/**
 * Temp config for one product: its template, else the chosen band. Judged
 * like a new delivery log on Express — an upper limit only.
 */
export function receiptCheckConfig(check, template = null) {
  if (template) return resolveNewDeliveryTempConfig(template.config)
  const band = check?.band || CHILLED
  if (band === AMBIENT) return { tempBand: AMBIENT }
  const preset = FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band]
  return { tempBand: band, tempMax: preset.tempMax }
}

export function isReceiptCheckOutOfRange(check, template = null) {
  return isAdHocTempOutOfRange(
    DELIVERIES,
    { value: check?.value },
    receiptCheckConfig(check, template),
  )
}

/**
 * First problem with the ticked products: `{ orderLineId, message }`, or
 * `{ orderLineId: null, message }` for the whole receipt, or null.
 */
export function validateReceiptChecks({
  products = [],
  checks = {},
  templates = [],
}) {
  const ticked = products.filter(
    p => isReceiptProductCheckable(p) && checks[p.orderLineId]?.checked,
  )
  if (!ticked.length) {
    return { orderLineId: null, message: 'Tick at least one product to check.' }
  }
  if (ticked.length > FOOD_SAFETY_DELIVERY_BATCH_MAX_CHECKS) {
    return {
      orderLineId: null,
      message: `Up to ${FOOD_SAFETY_DELIVERY_BATCH_MAX_CHECKS} products at a time.`,
    }
  }
  for (const product of ticked) {
    const check = checks[product.orderLineId]
    const template = matchReceiptDeliveryTemplate(
      templates,
      product.productName,
    )
    const config = receiptCheckConfig(check, template)
    const noTemp = check.value == null || check.value === ''
    if (noTemp && config.tempBand !== AMBIENT) {
      return {
        orderLineId: product.orderLineId,
        message: 'Enter a temperature.',
      }
    }
    const rejected = check.accepted === false
    if (!rejected && isReceiptCheckOutOfRange(check, template)) {
      const actionError = validateFoodSafetyCorrectiveDraft(check.action)
      if (actionError) {
        return { orderLineId: product.orderLineId, message: actionError }
      }
    }
  }
  return null
}

/** POST /deliveries/batch body — names and supplier come from the receipt. */
export function buildReceiptBatchBody({
  receipt,
  localDate,
  products = [],
  checks = {},
  templates = [],
}) {
  const lines = products.filter(
    p => isReceiptProductCheckable(p) && checks[p.orderLineId]?.checked,
  )
  return {
    purchaseOrderNumber: receipt.purchaseOrderNumber,
    supplierId: receipt.supplier?._id,
    ...(localDate ? { localDate } : {}),
    checks: lines.map(product => {
      const check = checks[product.orderLineId]
      const template = matchReceiptDeliveryTemplate(
        templates,
        product.productName,
      )
      const rejected = check.accepted === false
      const payload = { accepted: !rejected }
      if (check.value != null && check.value !== '') {
        payload.value = Number(check.value)
      }
      const reason = String(check.action?.notes || '').trim()
      if (rejected && reason) payload.rejectionReason = reason
      const out = { orderLineId: product.orderLineId, payload }
      if (template?._id) {
        out.definitionId = String(template._id)
      } else {
        out.configSnapshot = receiptCheckConfig(check, null)
      }
      const action =
        !rejected && isReceiptCheckOutOfRange(check, template)
          ? buildFoodSafetyCorrectiveActionBody(check.action || {})
          : null
      if (action) out.correctiveAction = action
      return out
    }),
  }
}

/** "3 delivery checks saved — 1 needs corrective action" */
export function formatReceiptBatchSaved(result) {
  const saved = Array.isArray(result?.logs) ? result.logs.length : 0
  const open = (result?.exceptions || []).filter(
    e => e && e.status !== 'resolved',
  ).length
  const head = `${saved} delivery check${saved === 1 ? '' : 's'} saved`
  if (!open) return head
  return `${head} — ${open} need${open === 1 ? 's' : ''} ${
    open === 1 ? 'corrective action' : 'corrective actions'
  }`
}

/** "Checked 09:40 · Accepted" on a product already checked from this receipt. */
export function formatReceiptLastCheck(lastCheck, { now } = {}) {
  if (!lastCheck?.checkedAt) return null
  const decision =
    lastCheck.accepted === false
      ? 'Rejected'
      : lastCheck.accepted === true
      ? 'Accepted'
      : null
  return [
    `Checked ${formatReceiptWhen(lastCheck.checkedAt, { now })}`,
    decision,
  ]
    .filter(Boolean)
    .join(' · ')
}
